import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '@/redux/store';
import dayjs, { Dayjs } from 'dayjs';
import { getAllListThunk, restartResumeAndReadingList } from '@/redux/slices/readingsResumesSlice';
import { initialContext, locationService, rangePresetsCalendar } from './constants';
import { formatDate, handleSelectModule as utilHandleSelectModule } from './utils';

export const useDashboardState = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch<AppDispatch>();

	// State variables
	const [contextValue, setContextValue] = useState<any>(initialContext);
	const [openBlockDialog, setOpenBlockDialog] = useState(false);
	const [openHydraulicProfilerDialog, setOpenHydraulicProfilerDialog] = useState(false);
	const [showInputCalendar, setShowInputCalendar] = useState(false);
	const [showCalendar, setShowCalendar] = useState(false);
	const [dates, setDates] = useState<any>(null);
	const [intervalRefresh, setIntervalRefresh] = useState(0);
	const [selectedPresetLabel, setSelectedPresetLabel] = useState<string | null>(null);
	const [locations, setLocations] = useState<any[]>([]);
	const [showLogoutModal, setShowLogoutModal] = useState(false);
	const [startDate, setStartDate] = useState<any>(dayjs(contextValue.selectedDate[0]));
	const [_, setSearchValues] = useState<any>();
	const [endDate, setEndDate] = useState<any>(dayjs(contextValue.selectedDate[1]));
	const [selectedTime1, setSelectedTime1] = useState<dayjs.Dayjs>(dayjs(contextValue.selectedDate[0]));
	const [selectedTime2, setSelectedTime2] = useState<dayjs.Dayjs>(dayjs(contextValue.selectedDate[1]));
	const [selectionPhase, setSelectionPhase] = useState<'start' | 'end'>('start');
	const [isOpen, setIsOpen] = useState(false);
	const [handleToggle, setHandleToggle] = useState<(() => void) | null>(null);
	const [showKeplerAnalysisModal, setShowKeplerAnalysisModal] = useState(false);
	const [showSewerAnalysisLauncherModal, setShowSewerAnalysisLauncherModal] = useState(false);
	const [messageModal, setMessageModal] = useState('');
	

	// Event handlers
	const disabledDate = useCallback(
		(current: Dayjs) => {
			if (!dates) {
				return false;
			}
			const tooLate = dates[0] && current.diff(dates[0], 'years') >= 2;
			const tooEarly = dates[1] && dates[1].diff(current, 'years') >= 2;
			return !!tooEarly || !!tooLate;
		},
		[dates],
	);

	const handleChangeInterval = (value: number) => {
		setIntervalRefresh(value);
	};

	const handleOpenBlockDialogChange = (newOpen: boolean) => {
		setOpenBlockDialog(newOpen);
	};

	const handleClickSelect = (value: any) => {
		const selectedValue = value;
		setContextValue((prev) => ({ ...prev, selectedLocation: selectedValue }));
	};

	const toggle = (toggle: () => void) => {
		if (!handleToggle) {
			setHandleToggle(() => toggle);
		}
		return null;
	};

	function handleChangeDate(item: any) {
		setContextValue((prev) => ({
			...prev,
			selectedDate: [
				dayjs(item[0]).format('YYYY-MM-DD HH:mm:ss'),
				dayjs(item[1]).format('YYYY-MM-DD HH:mm:ss'),
			],
		}));
		setShowCalendar(false);
	}

	const handleCalendarClick = () => {
		setShowInputCalendar(!showInputCalendar);
	};

	const handleSearch = () => {
		handleChangeDate(dates);
	};

	const handleTime1Change = (time: dayjs.Dayjs | null) => {
		if (time) {
			const parsedTime = time.set('minute', Math.round(time.minute() / 5) * 5);
			setSelectedTime1(parsedTime);
			setDates([
				dates[0].set('hour', parsedTime.hour()).set('minute', parsedTime.minute()),
				dates[1],
			]);
			setStartDate(startDate.set('hour', parsedTime.hour()).set('minute', parsedTime.minute()));
		}
	};

	const handleTime2Change = (time: dayjs.Dayjs | null) => {
		if (time) {
			const parsedTime = time.set('minute', Math.round(time.minute() / 5) * 5);
			setSelectedTime2(parsedTime);
			setDates([
				dates[0],
				dates[1].set('hour', parsedTime.hour()).set('minute', parsedTime.minute()),
			]);
			setEndDate(endDate.set('hour', parsedTime.hour()).set('minute', parsedTime.minute()));
		}
	};

	const openModalKeplerAnalysisHistorial = () => {
		setShowSewerAnalysisLauncherModal(true);
	};

	const handleReset = async () => {
		const hoy = new Date();
		const semana = 1000 * 60 * 60 * 24 * 1;
		const fec_init = hoy.getTime() - semana;
		const fec_end = hoy.getTime() + semana;
		setDates([dayjs(fec_init), dayjs(fec_end)]);
	};

	const handleSelectModuleWrapper = (moduleId: number) => {
		utilHandleSelectModule(moduleId, navigate, handleToggle || undefined);
	};

	// Effects
	useEffect(() => {
		const userId = localStorage.getItem('userId') || '';
		const resLocationByUser = locationService.getLocationsByUser(userId);
		resLocationByUser.then((locations) => {
			setLocations(locations);
		});
		if (contextValue.selectedSensors.length > 0) {
			dispatch(
				getAllListThunk(
					contextValue.selectedSensors,
					contextValue.selectedDate[0],
					contextValue.selectedDate[1],
				) as any,
			);
		}
		if (contextValue.selectedDate) {
			setDates([
				dayjs(contextValue.selectedDate[0])
					.set('hour', selectedTime1.hour())
					.set('minute', selectedTime1.minute()),
				dayjs(contextValue.selectedDate[1])
					.set('hour', selectedTime2.hour())
					.set('minute', selectedTime2.minute()),
			]);
		}
	}, []);

	useEffect(() => {
		let intervalRef: any = null;
		if (intervalRefresh > 0) {
			intervalRef = setInterval(() => {
				const preset = rangePresetsCalendar.find((item) => item.label === selectedPresetLabel);
				setContextValue((prev) => {
					return {
						...prev,
						fetchDashboardDataIndex: prev.fetchDashboardDataIndex + 1,
					};
				});
				if (preset) {
					const values = preset.value;
					setDates(values);
					setContextValue((prev) => {
						return {
							...prev,
							selectedDate: [
								values[0].format('YYYY-MM-DD HH:mm:ss'),
								values[1].format('YYYY-MM-DD HH:mm:ss'),
							],
						};
					});
				}
			}, intervalRefresh);
		}

		return () => {
			if (intervalRef) clearInterval(intervalRef);
		};
	}, [intervalRefresh]);

	useEffect(() => {
		const find: any = locations.find((loc: any) => loc.locationId == contextValue.selectedLocation);

		if (find?.defaultSelected) {
			localStorage.setItem(
				'selectedSensors',
				JSON.stringify(
					contextValue.selectedSensors.map((sensor: any) => {
						return JSON.stringify(sensor);
					}),
				),
			);
		}

		if (contextValue.selectedSensors.length > 0) {
			setContextValue((prev) => {
				return { ...prev, showParamsCard: true };
			});
		} else {
			setContextValue((prev) => {
				return { ...prev, showParamsCard: false };
			});
		}
	}, [contextValue.selectedSensors]);

	useEffect(() => {
		try {
			localStorage.setItem(
				'globalDate',
				formatDate(contextValue.selectedDate[0]) + '***' + formatDate(contextValue.selectedDate[1]),
			);
			setDates([dayjs(contextValue.selectedDate[0]), dayjs(contextValue.selectedDate[1])]);
		} catch (error) {
			console.error(error);
		}
	}, [contextValue.selectedDate]);

	return {
		// State
		contextValue,
		setContextValue,
		openBlockDialog,
		setOpenBlockDialog,
		openHydraulicProfilerDialog,
		setOpenHydraulicProfilerDialog,
		showInputCalendar,
		setShowInputCalendar,
		showCalendar,
		setShowCalendar,
		dates,
		setDates,
		intervalRefresh,
		setIntervalRefresh,
		selectedPresetLabel,
		setSelectedPresetLabel,
		locations,
		setLocations,
		showLogoutModal,
		setShowLogoutModal,
		startDate,
		setStartDate,
		endDate,
		setEndDate,
		selectedTime1,
		setSelectedTime1,
		selectedTime2,
		setSelectedTime2,
		selectionPhase,
		setSelectionPhase,
		isOpen,
		setIsOpen,
		handleToggle,
		setHandleToggle,
		showKeplerAnalysisModal,
		setShowKeplerAnalysisModal,
		showSewerAnalysisLauncherModal,
		setShowSewerAnalysisLauncherModal,
		messageModal,
		setMessageModal,
		// Handlers
		disabledDate,
		handleChangeInterval,
		handleOpenBlockDialogChange,
		handleClickSelect,
		toggle,
		handleChangeDate,
		handleCalendarClick,
		handleSearch,
		handleTime1Change,
		handleTime2Change,
		openModalKeplerAnalysisHistorial,
		handleReset,
		handleSelectModule: handleSelectModuleWrapper,
		// Navigation and dispatch
		navigate,
		location,
		dispatch,
	};
};
